import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import BrandHeader from '../../components/ui/BrandHeader';
import ReportFilters from './components/ReportFilters';
import ReportTypeCard from './components/ReportTypeCard';
import BackupManagement from './components/BackupManagement';
import ReportPreviewModal from './components/ReportPreviewModal';
import QuickStats from './components/QuickStats';
import Icon from '../../components/AppIcon';

const ReportsAndBackup = () => {
  const [filters, setFilters] = useState({
    academicYear: '2025-2026',
    fromDate: '',
    toDate: '',
    class: 'all',
    section: 'all',
    paymentStatus: 'all',
    term: 'all',
    paymentMethod: 'all'
  });

  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    reportType: '',
    data: []
  });

  const quickStats = {
    totalCollection: 0,
    pendingAmount: 0,
    pendingStudents: 0,
    onlinePayments: 0,
    onlineCount: 0,
    cashPayments: 0,
    cashCount: 0
  };

  // Cash vs UPI breakdown stats
  const paymentModeStats = {
    cash: { total: 0, count: 0, percentage: 0 },
    upi: { total: 0, count: 0, percentage: 0 },
    online: { total: 0, count: 0, percentage: 0 }
  };

  const reportTypes = [
    {
      title: "Payment Collection Report",
      description: "Comprehensive report of all fee collections with payment method breakdown and term-wise analysis",
      icon: "Receipt",
      stats: [
        { label: "Total Records", value: "—" },
        { label: "Collection", value: "—" }
      ]
    },
    {
      title: "Cash Collection Report",
      description: "Cash-only payment report for cash ledger reconciliation. Excludes UPI and online payments.",
      icon: "Banknote",
      stats: [
        { label: "Cash Records", value: "—" },
        { label: "Cash Total", value: "—" }
      ]
    },
    {
      title: "UPI Collection Report",
      description: "UPI and digital payment report for bank/UPI ledger reconciliation. Includes UTR numbers.",
      icon: "Smartphone",
      stats: [
        { label: "UPI Records", value: "—" },
        { label: "UPI Total", value: "—" }
      ]
    },
    {
      title: "Outstanding Balance Report",
      description: "Detailed list of students with pending fee payments including overdue amounts and due dates",
      icon: "AlertCircle",
      stats: [
        { label: "Pending Students", value: "—" },
        { label: "Outstanding", value: "—" }
      ]
    },
    {
      title: "Student Fee Structure Report",
      description: "Complete fee structure breakdown by class, section, and academic year with category-wise details",
      icon: "Users",
      stats: [
        { label: "Total Students", value: "—" },
        { label: "Classes", value: "—" }
      ]
    },
    {
      title: "Term-wise Collection Report",
      description: "Term-based collection analysis showing Term 1, Term 2, and Term 3 payment status and trends",
      icon: "Calendar",
      stats: [
        { label: "Terms", value: "3" },
        { label: "Avg Collection", value: "—" }
      ]
    },
    {
      title: "Payment Method Analysis",
      description: "Comparative analysis of Cash, UPI, and online payment methods with reconciliation data",
      icon: "CreditCard",
      stats: [
        { label: "UPI", value: "—" },
        { label: "Cash", value: "—" }
      ]
    },
    {
      title: "Class-wise Collection Report",
      description: "Class and section-wise fee collection summary with completion percentage and pending amounts",
      icon: "GraduationCap",
      stats: [
        { label: "Classes", value: "—" },
        { label: "Sections", value: "—" }
      ]
    }
  ];

  const backups = [];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      academicYear: '2025-2026',
      fromDate: '',
      toDate: '',
      class: 'all',
      section: 'all',
      paymentStatus: 'all',
      term: 'all',
      paymentMethod: 'all'
    });
  };

  const handleGenerateReport = () => {
    console.log('Generating report with filters:', filters);
  };

  const handlePreview = (reportType) => {
    setPreviewModal({ isOpen: true, reportType, data: [] });
  };

  const handleExportExcel = (reportType) => {
    console.log('Exporting to Excel:', reportType);
  };

  const handleExportPDF = (reportType) => {
    console.log('Exporting to PDF:', reportType);
  };

  const handleDownloadBackup = (backupId) => {
    console.log('Downloading backup:', backupId);
  };

  const handleTriggerBackup = () => {
    console.log('Triggering manual backup');
  };

  return (
    <>
      <Helmet>
        <title>Reports & Backup - SSVM Fees App</title>
        <meta name="description" content="Generate comprehensive financial reports and manage data backups for school fee management system" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <AdminSidebarNavigation />
        <div className="lg:ml-64">
          <BrandHeader variant="admin" />
          <main className="p-4 md:p-6 lg:p-8 pb-20 lg:pb-8">
            <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                  Reports & Backup
                </h1>
                <p className="text-sm md:text-base text-muted-foreground font-caption">
                  Generate comprehensive financial reports and manage data backups
                </p>
              </div>

              <QuickStats stats={quickStats} />

              {/* Cash vs UPI Breakdown */}
              <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="BarChart3" size={20} className="text-primary" />
                  <h2 className="text-base md:text-lg font-heading font-semibold text-foreground">
                    Payment Mode Breakdown
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Cash */}
                  <div className="bg-success/5 border border-success/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center">
                        <Icon name="Banknote" size={16} className="text-success" />
                      </div>
                      <span className="text-sm font-semibold text-foreground">Cash Ledger</span>
                    </div>
                    <p className="text-xl font-bold text-success data-text">
                      ₹{(paymentModeStats?.cash?.total / 100000)?.toFixed(2)} L
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-muted-foreground">{paymentModeStats?.cash?.count} transactions</span>
                      <span className="text-xs font-medium text-success">{paymentModeStats?.cash?.percentage}%</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-success/10 rounded-full">
                      <div className="h-full bg-success rounded-full" style={{ width: `${paymentModeStats?.cash?.percentage}%` }} />
                    </div>
                  </div>

                  {/* UPI */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Icon name="Smartphone" size={16} className="text-blue-600" />
                      </div>
                      <span className="text-sm font-semibold text-foreground">Bank / UPI Ledger</span>
                    </div>
                    <p className="text-xl font-bold text-blue-700 data-text">
                      ₹{(paymentModeStats?.upi?.total / 100000)?.toFixed(2)} L
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-muted-foreground">{paymentModeStats?.upi?.count} transactions</span>
                      <span className="text-xs font-medium text-blue-600">{paymentModeStats?.upi?.percentage}%</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-blue-100 rounded-full">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${paymentModeStats?.upi?.percentage}%` }} />
                    </div>
                  </div>

                  {/* Combined Total */}
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="TrendingUp" size={16} className="text-primary" />
                      </div>
                      <span className="text-sm font-semibold text-foreground">Combined Total</span>
                    </div>
                    <p className="text-xl font-bold text-primary data-text">
                      ₹{((paymentModeStats?.cash?.total + paymentModeStats?.upi?.total + paymentModeStats?.online?.total) / 100000)?.toFixed(2)} L
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-muted-foreground">
                        {paymentModeStats?.cash?.count + paymentModeStats?.upi?.count + paymentModeStats?.online?.count} total transactions
                      </span>
                      <span className="text-xs font-medium text-primary">100%</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-primary/10 rounded-full">
                      <div className="h-full bg-primary rounded-full w-full" />
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex items-start gap-2 text-xs text-muted-foreground bg-muted px-3 py-2 rounded-lg">
                  <Icon name="Info" size={13} className="flex-shrink-0 mt-0.5" />
                  <span>Cash report excludes UPI payments. UPI payments are tracked separately in the Bank/UPI Ledger to prevent cash mismatch.</span>
                </div>
              </div>

              <ReportFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onGenerate={handleGenerateReport}
                onReset={handleResetFilters}
              />

              <div>
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                      Available Reports
                    </h2>
                    <p className="text-xs md:text-sm text-muted-foreground font-caption">
                      Select a report type to preview or export
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {reportTypes?.map((report, index) => (
                    <ReportTypeCard
                      key={index}
                      title={report?.title}
                      description={report?.description}
                      icon={report?.icon}
                      stats={report?.stats}
                      onPreview={() => handlePreview(report?.title)}
                      onExportExcel={() => handleExportExcel(report?.title)}
                      onExportPDF={() => handleExportPDF(report?.title)}
                    />
                  ))}
                </div>
              </div>

              <BackupManagement
                backups={backups}
                onDownload={handleDownloadBackup}
                onTriggerBackup={handleTriggerBackup}
                lastBackupDate={new Date()?.toISOString()}
              />
            </div>
          </main>
        </div>
      </div>
      <ReportPreviewModal
        isOpen={previewModal?.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, reportType: '', data: [] })}
        reportData={previewModal?.data}
        reportType={previewModal?.reportType}
      />
    </>
  );
};

export default ReportsAndBackup;